import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { TRIP_TEMPLATES } from '../data/tripTemplates';
import { useSEO } from '../hooks/useSEO';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';

const DestinationGuidePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const template = TRIP_TEMPLATES.find(t => t.id === slug);

  useSEO(
    template
      ? {
          title: `Luxury ${template.destination} Itinerary — Aura Traveler`,
          description: template.description || `Curated luxury travel itinerary for ${template.destination}. AI-generated, magazine-quality travel design.`,
          canonical: `https://www.auratraveler.com/destinations/${template.id}`,
          ogImage: template.heroImage,
          breadcrumbs: [
            { name: 'Home', url: 'https://www.auratraveler.com/' },
            { name: 'Destinations', url: 'https://www.auratraveler.com/destinations' },
            { name: template.destination, url: `https://www.auratraveler.com/destinations/${template.id}` },
          ],
        }
      : {
          title: 'Destination Not Found — Aura Traveler',
          description: 'The requested destination could not be found.',
        }
  );

  if (!template) {
    return (
      <div className="min-h-screen bg-charcoal text-white">
        <SiteNav />
        <div className="flex flex-col items-center justify-center py-32 px-6 text-center">
          <h1 className="font-display text-3xl mb-4">Destination Not Found</h1>
          <p className="font-serif text-gray-400 italic mb-8">The requested destination could not be found in our collection.</p>
          <Link to="/destinations" className="inline-block bg-gold text-charcoal px-8 py-3 font-display text-sm uppercase tracking-widest hover:bg-white transition-colors rounded-sm">
            Browse All Destinations
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "TravelAction",
    "name": `Luxury ${template.destination} Itinerary`,
    "description": template.description,
    "location": {
      "@type": "Place",
      "name": template.destination
    }
  };

  return (
    <div className="min-h-screen bg-charcoal text-white">
      <SiteNav />
      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <main>
      {/* Hero */}
      <section className="relative h-[60vh] overflow-hidden">
        <img src={template.heroImage} alt={`Scenic view of ${template.destination}`} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
          <p className="font-display text-xs uppercase tracking-[0.3em] text-gold mb-2">{template.category} · {template.duration}</p>
          <h1 className="font-display text-4xl md:text-6xl text-white mb-3">{template.destination}</h1>
          <p className="font-serif text-lg text-gray-300 max-w-2xl italic">{template.description}</p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        {/* Quick Facts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          <div className="bg-white/5 border border-white/10 rounded-sm p-4 text-center">
            <p className="text-gold text-xs font-display uppercase tracking-widest mb-1">Duration</p>
            <p className="text-white font-serif">{template.duration}</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-sm p-4 text-center">
            <p className="text-gold text-xs font-display uppercase tracking-widest mb-1">Best Season</p>
            <p className="text-white font-serif">{template.bestSeason || 'Year-round'}</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-sm p-4 text-center">
            <p className="text-gold text-xs font-display uppercase tracking-widest mb-1">Price Range</p>
            <p className="text-white font-serif">{template.priceRange || 'From $2,500'}</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-sm p-4 text-center">
            <p className="text-gold text-xs font-display uppercase tracking-widest mb-1">Style</p>
            <p className="text-white font-serif capitalize">{template.category}</p>
          </div>
        </div>

        {/* Highlights */}
        {template.highlights && template.highlights.length > 0 && (
          <div className="mb-16">
            <h2 className="font-display text-2xl text-white mb-6 uppercase tracking-widest">Highlights</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {template.highlights.map((h: string, i: number) => (
                <div key={i} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-sm p-4">
                  <span className="text-gold mt-0.5">&#10022;</span>
                  <p className="font-serif text-gray-300">{h}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center py-12 border-t border-white/10">
          <h2 className="font-display text-2xl text-white mb-3">Ready to Experience {template.destination}?</h2>
          <p className="font-serif text-gray-400 mb-6 italic">Let our AI concierge craft your perfect itinerary</p>
          <Link to="/" className="inline-block bg-gold text-charcoal px-8 py-4 font-display text-sm uppercase tracking-widest hover:bg-white transition-colors rounded-sm">
            Create Your {template.destination.split(',')[0]} Itinerary
          </Link>
        </div>
      </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default DestinationGuidePage;
